"use client";

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { THEME_CLASSES } from "../design";
import { LIGHT_PALETTE } from "../design/lightTheme/colors";
import { ProtonPalette, ProtonStyleSheet, ProtonTheme } from "../design/types";
import { generateStylesheet } from "../design/generateStylesheet";

interface ThemeContextType {
  theme: ProtonTheme;
  className: string;
  style: ProtonStyleSheet;
  palette: ProtonPalette;
  hasPalette?: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  theme: ProtonTheme;
  children: ReactNode;
  palette?: ProtonPalette;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  theme,
  children,
  palette = LIGHT_PALETTE,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const themeClass = THEME_CLASSES[theme];
  const themeVariables = useMemo(
    () => generateStylesheet(palette, theme),
    [palette, theme]
  );

  useEffect(() => {
    // Apply the theme class and css variables to the container div so that it is only scoped
    // to the ThemeProvider children.
    if (containerRef.current) {
      containerRef.current.className = themeClass;

      Object.entries(themeVariables).forEach(([key, value]) => {
        containerRef.current!.style.setProperty(key, value as string);
      });
    }
  }, [theme, themeClass, palette, themeVariables]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        style: themeVariables,
        palette,
        className: themeClass,
        hasPalette: Boolean(palette),
      }}
    >
      <div ref={containerRef}>{children}</div>
    </ThemeContext.Provider>
  );
};
